/**
 * Progress Page
 * View child's learning progress and statistics
 */

import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Calendar } from 'lucide-react';
import { analyticsService, childService, getErrorMessage } from '../services';
import type { ChildActivitySummary } from '@kids-lms/types';
import type { ChildProfileWithConsent } from '@kids-lms/types';
import MindoraCharacter from '../components/character/MindoraCharacter';
import KidLoader from '../components/ui/KidLoader';
import Confetti from '../components/effects/Confetti';
import TornPaperCard from '../components/ui/TornPaperCard';
import StickerBadge from '../components/ui/StickerBadge';
import { WavyDivider } from '../components/decorations';
import { StaggerList, StaggerItem } from '../components/ui/StaggerList';
import AnimatedNumber from '../components/ui/AnimatedNumber';
import LiquidProgressBar from '../components/ui/LiquidProgressBar';
import { useCelebration } from '../hooks/useCelebration';
import { useTouchFeedback } from '../hooks/useTouchFeedback';
import BadgeReveal from '../components/effects/BadgeReveal';
import Fireworks from '../components/effects/Fireworks';
import ScreenShake from '../components/effects/ScreenShake';

export default function ProgressPage() {
  const { childId } = useParams<{ childId: string }>();
  const [child, setChild] = useState<ChildProfileWithConsent | null>(null);
  const [summary, setSummary] = useState<ChildActivitySummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [dateRange, setDateRange] = useState<'week' | 'month' | 'all'>('week');
  const [showConfetti] = useState(false);
  const celebration = useCelebration();
  const touchFeedback = useTouchFeedback();

  useEffect(() => {
    if (childId) {
      loadData();
    }
  }, [childId, dateRange]);

  const loadData = async () => {
    if (!childId) return;

    try {
      setLoading(true);
      const childData = await childService.getChild(childId);
      setChild(childData);

      let startDate: string | undefined;
      const now = new Date();

      if (dateRange === 'week') {
        const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        startDate = weekAgo.toISOString().split('T')[0];
      } else if (dateRange === 'month') {
        const monthAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
        startDate = monthAgo.toISOString().split('T')[0];
      }

      const summaryData = await analyticsService.getChildSummary(childId, startDate);
      setSummary(summaryData);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px] bg-kid-bg">
        <KidLoader message="Loading your stars..." />
      </div>
    );
  }

  const subjectEmojis: Record<string, string> = {
    math: '🔢', science: '🔬', english: '📚', history: '🏛️',
    art: '🎨', coding: '💻', general: '✨', geography: '🌍', music: '🎵',
  };

  const subjectBarColors: Record<string, string> = {
    math: 'var(--kid-ocean)', science: 'var(--kid-meadow)', english: 'var(--kid-violet)',
    history: 'var(--kid-tangerine)', art: 'var(--kid-berry)', coding: 'var(--kid-coral)',
    general: 'var(--kid-sky)', geography: 'var(--kid-ocean)', music: 'var(--kid-violet)',
  };

  const statCards = [
    { emoji: '💬', label: 'Conversations', value: summary?.conversations.total || 0, gradient: 'from-kid-ocean to-kid-sky' },
    { emoji: '❓', label: 'Questions Asked', value: summary?.conversations.totalMessages || 0, gradient: 'from-kid-meadow to-kid-ocean' },
    { emoji: '⏱️', label: 'Avg. Session', value: `${Math.round(summary?.conversations.avgSessionMinutes || 0)} min`, gradient: 'from-kid-violet to-kid-berry' },
    { emoji: '📖', label: 'Subjects', value: summary?.subjects.length || 0, gradient: 'from-kid-dandelion to-kid-tangerine' },
  ];

  return (
    <ScreenShake trigger={celebration.showScreenShake}>
    <div className="max-w-4xl mx-auto p-6 bg-kid-bg min-h-screen">
      <Confetti trigger={showConfetti || celebration.active} count={celebration.confettiCount || undefined} />
      <Fireworks trigger={celebration.showFireworks} />

      <div className="mb-6">
        <Link
          to="/parent"
          onPointerDown={touchFeedback.onPointerDown}
          className="inline-flex items-center gap-2 text-kid-text-soft hover:text-kid-text font-bold touch-kid touch-glow touch-slop"
        >
          <span className="inline-flex flex-col items-center gap-0.5">
            <ArrowLeft className="w-5 h-5" strokeWidth={2.25} />
            <span className="text-xs font-bold leading-tight">Back</span>
          </span>
          Back to Dashboard
        </Link>
      </div>

      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
        {/* Header with mascot */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <MindoraCharacter mood="celebrating" size="md" />
            <div>
              <h1 className="text-kid-2xl font-display text-kid-text">
                {child?.name}'s Stars! ⭐
              </h1>
              <p className="text-kid-text-soft text-kid-base">Learning statistics and achievements</p>
            </div>
          </div>

          {/* Date Range Filter */}
          <div className="flex gap-2">
            {(['week', 'month', 'all'] as const).map((range) => (
              <button
                key={range}
                onClick={() => setDateRange(range)}
                onPointerDown={touchFeedback.onPointerDown}
                className={`btn-squishy px-5 py-3 rounded-kid-sm text-kid-sm font-bold transition-colors touch-kid touch-glow ${
                  dateRange === range
                    ? 'bg-kid-violet text-white shadow-kid'
                    : 'bg-white text-kid-text-soft border-2 border-kid-ocean/15 hover:bg-kid-bg-alt'
                }`}
              >
                {range === 'week' ? 'This Week' : range === 'month' ? 'This Month' : 'All Time'}
              </button>
            ))}
          </div>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-kid-coral/10 border-2 border-kid-coral/20 text-kid-coral rounded-kid font-bold">
            {error}
          </div>
        )}

        {/* Stats Overview — torn paper cards with sticker badges */}
        <StaggerList className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          {statCards.map((card, i) => (
            <StaggerItem key={card.label}>
              <TornPaperCard>
                <div className="flex flex-col items-center text-center">
                  <StickerBadge
                    color={`var(--kid-${['ocean', 'meadow', 'violet', 'tangerine'][i]})`}
                    rotation={[-3, 2, -2, 3][i]}
                    className="mb-3 min-w-[56px] min-h-[56px]"
                  >
                    <span className="text-2xl">{card.emoji}</span>
                  </StickerBadge>
                  <p className="text-kid-text-soft text-kid-sm font-bold">{card.label}</p>
                  <p className="text-kid-xl font-display text-kid-text">
                    {typeof card.value === 'number' ? (
                      <AnimatedNumber value={card.value} />
                    ) : (
                      card.value
                    )}
                  </p>
                </div>
              </TornPaperCard>
            </StaggerItem>
          ))}
        </StaggerList>

        {/* Subject Breakdown */}
        <div className="bg-white rounded-kid shadow-kid p-6 mb-6 border-2 border-kid-ocean/10">
          <h2 className="text-kid-lg font-display text-kid-text mb-4 flex items-center gap-2">
            📊 Subject Breakdown
          </h2>
          {summary?.subjects && summary.subjects.length > 0 ? (
            <div className="space-y-4">
              {summary.subjects.map((subject) => {
                const total = summary.subjects.reduce((sum, s) => sum + s.count, 0);
                const percentage = total > 0 ? Math.round((subject.count / total) * 100) : 0;
                return (
                  <div key={subject.subject}>
                    <div className="flex justify-between text-kid-sm mb-1">
                      <span className="text-kid-text font-bold capitalize">
                        {subjectEmojis[subject.subject] || '📝'} {subject.subject}
                      </span>
                      <span className="text-kid-text-soft font-bold">
                        {subject.count} conversations ({percentage}%)
                      </span>
                    </div>
                    <LiquidProgressBar
                      percentage={percentage}
                      color={subjectBarColors[subject.subject] || '#6B7280'}
                    />
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-8">
              <MindoraCharacter mood="waiting" size="md" />
              <p className="text-kid-text-soft text-kid-base mt-2">No subject data available yet</p>
            </div>
          )}
        </div>

        {/* Wavy divider between subject breakdown and daily activity */}
        <WavyDivider className="my-4" color="var(--kid-violet)" />

        {/* Daily Activity */}
        <div className="bg-white rounded-kid shadow-kid p-6 border-2 border-kid-ocean/10">
          <h2 className="text-kid-lg font-display text-kid-text mb-4 flex items-center gap-2">
            <Calendar className="w-5 h-5 text-kid-ocean" strokeWidth={2.25} />
            Daily Activity
          </h2>
          {summary?.dailyActivity && summary.dailyActivity.length > 0 ? (
            <div className="space-y-3">
              {summary.dailyActivity.slice(-7).map((day) => (
                <div key={day.date} className="flex items-center gap-4">
                  <span className="text-kid-sm text-kid-text-soft w-24 font-bold">
                    {new Date(day.date).toLocaleDateString('en-US', {
                      weekday: 'short',
                      month: 'short',
                      day: 'numeric',
                    })}
                  </span>
                  <div className="flex-1 h-7 bg-kid-bg rounded-kid-sm overflow-hidden">
                    <div
                      className="h-full rounded-kid-sm flex items-center justify-end pr-2"
                      style={{
                        background: 'linear-gradient(90deg, var(--kid-ocean), var(--kid-violet))',
                        width: `${Math.min(
                          (day.conversations / Math.max(...summary.dailyActivity.map((d) => d.conversations), 1)) *
                            100,
                          100
                        )}%`,
                        minWidth: day.conversations > 0 ? '40px' : '0',
                      }}
                    >
                      {day.conversations > 0 && (
                        <span className="text-xs text-white font-bold">{day.conversations}</span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <MindoraCharacter mood="waiting" size="md" />
              <p className="text-kid-text-soft text-kid-base mt-2">No activity data available yet</p>
            </div>
          )}
        </div>

        {/* Achievements — colorful gradient when unlocked */}
        <div className="mt-6 bg-gradient-to-r from-kid-dandelion/20 to-kid-tangerine/20 rounded-kid p-6 border-2 border-kid-dandelion/30">
          <div className="flex items-center gap-3 mb-4">
            <MindoraCharacter mood="celebrating" size="sm" />
            <h2 className="text-kid-lg font-display text-kid-text">Achievements 🏆</h2>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            {[
              { name: 'First Question', emoji: '🌟', unlocked: true, gradient: 'from-kid-dandelion to-kid-tangerine' },
              { name: '10 Conversations', emoji: '🎯', unlocked: false, gradient: 'from-kid-ocean to-kid-sky' },
              { name: 'All Subjects', emoji: '🧠', unlocked: false, gradient: 'from-kid-violet to-kid-berry' },
              { name: '7-Day Streak', emoji: '🔥', unlocked: false, gradient: 'from-kid-coral to-kid-tangerine' },
            ].map((ach, i) => (
              <BadgeReveal key={ach.name} trigger={ach.unlocked}>
                <motion.div
                  whileHover={ach.unlocked ? { scale: 1.05 } : undefined}
                  className={`text-center p-4 rounded-kid ${
                    ach.unlocked
                      ? `bg-gradient-to-br ${ach.gradient} text-white shadow-kid kid-interactive touch-glow`
                      : 'bg-white/50 opacity-50 border-2 border-kid-ocean/10'
                  }`}
                >
                  <motion.span
                    className="text-4xl block mb-2"
                    animate={ach.unlocked ? { y: [0, -4, 0] } : undefined}
                    transition={ach.unlocked ? { repeat: Infinity, duration: 2, delay: i * 0.3 } : undefined}
                  >
                    {ach.emoji}
                  </motion.span>
                  <p className={`text-kid-sm font-display ${ach.unlocked ? 'text-white' : 'text-kid-text-soft'}`}>
                    {ach.name}
                  </p>
                </motion.div>
              </BadgeReveal>
            ))}
          </div>
        </div>
      </motion.div>
    </div>
    </ScreenShake>
  );
}
